// services/studySets.ts
//
// The study-set endpoints (ORA-677). A set is the thread's half of a study
// session: a handful of items rendered inline, the rest of the same filter
// waiting behind the footer button.
//
// THE CONTRACT THIS CLIENT IS WRITTEN AGAINST — the backend must match it:
//
//   POST /api/v5/study-sets/           200 { set, blockId, messageId, limits }
//                                        (review form)
//   GET  /api/v5/study-sets/:id          200 { set }
//   POST /api/v5/study-sets/:id/launch   200 { launched: true, set, target }
//                                        200 { launched: false, reason, set }
//   POST /api/v5/study-sets/:id/return   200 { set, eventBlockId }
//
// Every route is owner-only (another student's set is a 404), and `set` is the
// same `study_set` block payload the serve path streams — so it is read through
// the same tolerant parser the registry uses rather than trusted field by
// field. A set that will not parse is reported as a failure instead of being
// half-rendered.

import axiosInstance from './axiosCreator';
import { parseStudySetBlockPayload } from '@/components/ora/Chat/Companion/blocks/chatBlockPayload';
import type { ChatStudySetBlockPayload } from '@/types/chatBlocks';
import type { ChatNavigationTarget } from '@/types/chatStream';

export const STUDY_SETS_ROUTE = '/api/v5/study-sets';

/** Why a launch came back without a target. */
export type StudySetLaunchRefusal =
  | 'limit_reached'
  /** A lazy set whose filter draws nothing at launch (409, contract 2026-09-17). */
  | 'launch_unavailable'
  | 'unavailable';

export type StudySetLaunchResult =
  | {
      launched: true;
      set: ChatStudySetBlockPayload;
      target: ChatNavigationTarget;
    }
  | {
      launched: false;
      reason: StudySetLaunchRefusal;
      set: ChatStudySetBlockPayload | null;
    };

/** What coming back from a set wrote, if anything. */
export interface StudySetReturnResult {
  set: ChatStudySetBlockPayload;
  /** The `set_result` row the return wrote; null when nothing changed. */
  eventBlockId: string | null;
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** The `set` of a response body, parsed, or null when there isn't one. */
const readSet = (body: unknown): ChatStudySetBlockPayload | null => {
  const set = asRecord(asRecord(body)?.set);
  return set ? parseStudySetBlockPayload(set) : null;
};

const readTarget = (body: unknown): ChatNavigationTarget | null => {
  const target = asRecord(asRecord(body)?.target);
  const route = target?.route;
  if (typeof route !== 'string' || route === '') return null;

  const params = asRecord(target?.params) ?? {};

  const stringParams = Object.fromEntries(
    Object.entries(params).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string'
    )
  );

  return { route, params: stringParams };
};

/** The HTTP status an axios rejection carries, or null when it has none. */
const responseStatus = (error: unknown): number | null => {
  const status = asRecord(asRecord(error)?.response)?.status;
  return typeof status === 'number' ? status : null;
};

const setPath = (setId: string) =>
  `${STUDY_SETS_ROUTE}/${encodeURIComponent(setId)}`;

/**
 * Review sets minted from a missed question of an in-chat set (ORA-830/831).
 *
 * The set the student missed the question in, the question itself, and which
 * half of its linked content to study — and nothing else. Every id is
 * re-derived server-side: the source set must be the caller's, the vignette must
 * be one of its items, that item must be answered and wrong, and the variant /
 * card ids are intersected with the student's own scheduling rather than taken
 * on trust.
 */
export interface CreateReviewStudySetArgs {
  /** The in-chat question set holding the missed question. */
  fromSetId: string;
  /** The missed question; must be one of that set's items. */
  sourceVignetteId: string;
  kind: 'question' | 'flashcard';
  /**
   * The variant the modal is previewing (`kind: 'question'`). Omitted, the
   * server resolves it; a value that is not the scheduled one is refused, which
   * is how a stale badge is caught.
   */
  variantId?: string | null;
  /**
   * The cards the curation modal left enabled (`kind: 'flashcard'`, ORA-832).
   * Omitted, the server uses every assigned card; sent, the ids are intersected
   * with the assigned list in the server's order — never added to — so an id
   * that has since been unassigned is dropped rather than honoured. `[]` is not
   * malformed: it is the same refusal a stale badge gets.
   *
   * At most 10; more is a 400. The read behind the badge caps the candidates at
   * 8, so the modal cannot exceed it.
   */
  flashcardIds?: string[] | null;
}

/** Why a review set could not be made. */
export type StudySetReviewRefusal =
  /** The item is unanswered, or was answered correctly. */
  | 'item_not_missed'
  /** Nothing is queued for that question any more. */
  | 'no_scheduled_variant'
  /** The badge is offering a variant the server no longer schedules. */
  | 'variant_mismatch'
  /** Every linked card has since been unassigned. */
  | 'no_linked_flashcards'
  /** A self-assessment attempt is open; nothing may be served beside it. */
  | 'live_attempt'
  /** The content behind the badge is gone: nothing was written, and nothing
   * will be. The badge is spent rather than temporarily refused. */
  | 'content_unavailable'
  /** Not the caller's set, not a question set, or not one of its items. */
  | 'not_found'
  /** Over the per-session create budget. */
  | 'rate_limited'
  /** Anything else, including a backend that does not serve this form yet. */
  | 'unavailable';

export type CreateReviewStudySetResult =
  | {
      ok: true;
      /**
       * The set exactly as the server sent it — the `study_set` block payload
       * the thread renders from, so it goes to the slice unparsed the way a
       * streamed block does.
       */
      set: Record<string, unknown>;
      /** The `study_set` block the server put in the thread. */
      blockId: string;
      /**
       * The empty assistant message that block hangs off — the row the thread
       * rates and the id a re-read will bring back. A repeat tap names the one
       * the first tap wrote. Null from a backend that does not send it yet.
       */
      messageId: string | null;
    }
  | { ok: false; reason: StudySetReviewRefusal };

/** The refusal an error body names, when it names one this client knows. */
const readRefusal = (error: unknown): StudySetReviewRefusal => {
  const response = asRecord(asRecord(error)?.response);
  const status = response?.status;

  if (status === 404) return 'not_found';
  if (status === 429) return 'rate_limited';

  if (status === 422) {
    const reason = asRecord(response?.data)?.reason;

    if (
      reason === 'item_not_missed' ||
      reason === 'no_scheduled_variant' ||
      reason === 'variant_mismatch' ||
      reason === 'no_linked_flashcards' ||
      reason === 'live_attempt' ||
      reason === 'content_unavailable'
    ) {
      return reason;
    }
  }

  return 'unavailable';
};

/**
 * Study a missed question's linked content right here, as a new set in the
 * thread (ORA-831).
 *
 * Idempotent server-side on the source set and question: a repeat comes back
 * with the same set and the same block, which is the block to scroll to rather
 * than a second one to insert. It is not a lock, though, so the control that
 * asked must stay disabled while this is in flight.
 *
 * Resolves rather than throws, so the caller can say why: a 422 reason is a
 * badge that has gone stale, not a request to retry.
 */
export const createReviewStudySet = async (
  args: CreateReviewStudySetArgs
): Promise<CreateReviewStudySetResult> => {
  let response;

  try {
    // The trailing slash is what ora-be registers the create on; the route
    // answers either form, and this is the one the contract above names.
    response = await axiosInstance.post(`${STUDY_SETS_ROUTE}/`, {
      fromSetId: args.fromSetId,
      sourceVignetteId: args.sourceVignetteId,
      kind: args.kind,
      ...(args.kind === 'question' && args.variantId
        ? { variantId: args.variantId }
        : {}),
      ...(args.kind === 'flashcard' && args.flashcardIds
        ? { flashcardIds: args.flashcardIds }
        : {}),
    });
  } catch (error) {
    return { ok: false, reason: readRefusal(error) };
  }

  const body = asRecord(response.data);
  const set = asRecord(body?.set);
  const blockId = body?.blockId;
  const messageId = body?.messageId;

  // A set that will not parse, or a set with no block to show it in, is a
  // malformed answer rather than a refusal the student caused — but there is
  // still nothing to render, so it is reported the same way.
  if (!set || !parseStudySetBlockPayload(set) || typeof blockId !== 'string') {
    console.error('Malformed review study-set response');
    return { ok: false, reason: 'unavailable' };
  }

  return {
    ok: true,
    set,
    blockId,
    messageId: typeof messageId === 'string' ? messageId : null,
  };
};

/** One set, as the block that renders it. Throws when the read fails. */
export const getStudySet = async (
  setId: string
): Promise<ChatStudySetBlockPayload> => {
  const response = await axiosInstance.get(setPath(setId));
  const set = readSet(response.data);
  if (!set) throw new Error('That study set could not be loaded.');

  return set;
};

/**
 * Open the full session for a set.
 *
 * Idempotent server-side: a set that was already launched comes back with the
 * target it was launched with, which is what makes "Resume" the same call as
 * "Open full session". A free student at their daily QBank allowance is refused
 * rather than sent into a session they cannot answer in.
 */
export const launchStudySet = async (
  setId: string
): Promise<StudySetLaunchResult> => {
  let response;

  try {
    response = await axiosInstance.post(`${setPath(setId)}/launch`, {});
  } catch (error) {
    // The one refusal the server sends as an error rather than a body: a lazy
    // set whose filter now draws nothing. It is not a failure to retry — there
    // is nothing there — so it is reported as a refusal like the allowance is.
    if (responseStatus(error) === 409) {
      return { launched: false, reason: 'launch_unavailable', set: null };
    }

    throw error;
  }
  const body = asRecord(response.data);
  const set = readSet(response.data);
  const target = readTarget(response.data);

  if (body?.launched === true && target && set) {
    return { launched: true, set, target };
  }

  const reason = body?.reason;

  return {
    launched: false,
    reason: reason === 'limit_reached' ? 'limit_reached' : 'unavailable',
    set,
  };
};

/**
 * Report that the student came back from a set, so the thread can show what
 * they did. The server decides whether that is worth a row; `eventBlockId` is
 * null when nothing has changed since the last return.
 */
export const returnFromStudySet = async (
  setId: string
): Promise<StudySetReturnResult> => {
  const response = await axiosInstance.post(`${setPath(setId)}/return`, {});
  const set = readSet(response.data);
  if (!set) throw new Error('That study set could not be loaded.');

  const eventBlockId = asRecord(response.data)?.eventBlockId;

  return {
    set,
    eventBlockId: typeof eventBlockId === 'string' ? eventBlockId : null,
  };
};
